/**
 * 360° Viewer Application Bootstrap
 * Reads query parameters and initializes the viewer programmatically
 */
import Viewer360 from './Viewer360.js';

// Parse query parameters
function getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
        mode: params.get('mode') || 'edit',
        galleryId: params.get('galleryId') || params.get('gallery_id') || params.get('experienceId') || params.get('id') || null,
        jwtToken: params.get('jwtToken') || import.meta.env.VITE_JWT_TOKEN || null,
        apiEndpoint: params.get('apiEndpoint') || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
        projectId: params.get('projectId') || params.get('project_id') || null,
        autoAttach: params.get('autoAttach') === 'true' || params.get('auto_attach') === 'true' || false
    };
}

// Initialize the viewer
async function bootstrap() {
    try {
        const config = getQueryParams();

        // Validate authentication credentials based on mode
        // Edit mode: requires JWT token
        // View mode: no authentication required (always public)
        if (config.mode === 'edit') {
            if (!config.jwtToken) {
                showJwtMissingError();
                return;
            }
        }
        // View mode: No validation needed - always public

        console.log('[Bootstrap] Initializing Viewer360 with config:', {
            mode: config.mode,
            galleryId: config.galleryId,
            hasJwtToken: !!config.jwtToken,
            apiEndpoint: config.apiEndpoint,
            projectId: config.projectId,
            autoAttach: config.autoAttach
        });

        // Create viewer instance
        const viewer = new Viewer360({
            mode: config.mode,
            galleryId: config.galleryId,
            jwtToken: config.jwtToken,
            container: '#app',
            apiEndpoint: config.apiEndpoint,
            projectId: config.projectId,
            autoAttach: config.autoAttach,

            // Lifecycle hooks for debugging
            onInit: () => {
                console.log('[Bootstrap] Viewer initialized');
            },
            onLoad: (data) => {
                console.log('[Bootstrap] Experience loaded:', data);
            },
            onSave: (result) => {
                console.log('[Bootstrap] Experience saved:', result);
            },
            onError: (error) => {
                console.error('[Bootstrap] Error:', error);
                // You could show a user-friendly error UI here
            },
            onImageChange: (index) => {
                console.log('[Bootstrap] Image changed to index:', index);
            }
        });

        // Initialize the viewer (it will store itself in window.viewer360)
        await viewer.init();

        console.log('[Bootstrap] Viewer ready!');

    } catch (error) {
        console.error('[Bootstrap] Failed to initialize viewer:', error);

        // Show error to user
        showError(error.message);
    }
}

// Show error message to user with unified design
function showError(message) {
    showAuthError('Erreur d\'Initialisation', message);
}

// Show JWT missing error with instructions
function showJwtMissingError() {
    showAuthError(
        'Authentification Requise',
        'Le <strong style="color: #60a5fa;">mode édition</strong> nécessite un JWT token Supabase pour accéder aux fonctionnalités d\'édition.',
        true // Show developer instructions
    );
}

// Unified error display function
function showAuthError(title, message, showInstructions = false) {
    // Hide gallery if visible
    const gallery = document.getElementById('gallery');
    if (gallery) {
        gallery.style.display = 'none';
    }

    // Add dark background overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        z-index: 9998;
    `;
    document.body.appendChild(overlay);

    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%);
        backdrop-filter: blur(20px);
        border: 2px solid rgba(59, 130, 246, 0.3);
        border-radius: 16px;
        padding: 32px;
        color: white;
        max-width: 600px;
        width: 90%;
        z-index: 9999;
        font-family: system-ui, -apple-system, sans-serif;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    `;

    // Build instructions HTML
    let instructionsHTML = '';
    if (showInstructions) {
        instructionsHTML = `
            <div style="background: rgba(59, 130, 246, 0.1); border-left: 3px solid #3b82f6; padding: 12px 16px; border-radius: 6px; margin-bottom: 16px;">
                <p style="margin: 0; color: #cbd5e1; font-size: 14px; line-height: 1.5;">
                    <strong style="color: #60a5fa;">💡 Pour les développeurs :</strong> Ajoutez le paramètre <code style="background: rgba(15, 23, 42, 0.8); padding: 2px 6px; border-radius: 4px; color: #a78bfa; font-family: monospace; font-size: 13px;">jwtToken</code> à l'URL ou configurez-le dans votre fichier <code style="background: rgba(15, 23, 42, 0.8); padding: 2px 6px; border-radius: 4px; color: #a78bfa; font-family: monospace; font-size: 13px;">.env</code>
                </p>
            </div>

            <div style="background: rgba(15, 23, 42, 0.8); border-radius: 8px; padding: 12px; margin-bottom: 16px;">
                <p style="margin: 0 0 8px 0; color: #94a3b8; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Exemple d'URL :</p>
                <code style="display: block; color: #a78bfa; font-family: 'Courier New', monospace; font-size: 13px; line-height: 1.5; word-break: break-all;">
                    ${window.location.origin}${window.location.pathname}?mode=edit&jwtToken=<span style="color: #60a5fa;">votre-jwt-token</span>&galleryId=<span style="color: #60a5fa;">id-optionnel</span>
                </code>
            </div>

            <p style="margin: 0; color: #94a3b8; font-size: 14px; line-height: 1.5;">
                Le JWT token est fourni automatiquement par votre application parente via Supabase.
            </p>
        `;
    } else {
        instructionsHTML = `
            <p style="margin: 0; color: #94a3b8; font-size: 14px; line-height: 1.5;">
                Veuillez vérifier votre authentification ou contacter <strong style="color: #60a5fa;">Inoveo3D</strong> pour obtenir de l'aide.
            </p>
        `;
    }

    errorDiv.innerHTML = `
        <div style="display: flex; align-items: center; margin-bottom: 20px;">
            <div style="
                width: 48px;
                height: 48px;
                background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 16px;
                flex-shrink: 0;
            ">
                <svg style="width: 28px; height: 28px; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                </svg>
            </div>
            <div>
                <h3 style="margin: 0 0 4px 0; font-size: 24px; font-weight: 700; background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
                    ${title}
                </h3>
                <p style="margin: 0; font-size: 14px; color: #94a3b8;">L'authentification est nécessaire pour utiliser le mode édition</p>
            </div>
        </div>

        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(59, 130, 246, 0.2); border-radius: 12px; padding: 20px; margin-bottom: 24px;">
            <p style="margin: 0 0 16px 0; color: #e2e8f0; font-size: 15px; line-height: 1.6;">
                ${message}
            </p>

            ${instructionsHTML}
        </div>

        <a href="https://inoveo3d.com/contact" target="_blank" rel="noopener noreferrer" style="
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 12px 20px;
            background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
            border: none;
            border-radius: 8px;
            color: white;
            font-weight: 600;
            cursor: pointer;
            font-size: 14px;
            text-decoration: none;
            transition: transform 0.2s, box-shadow 0.2s;
        " onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 10px 25px -5px rgba(59, 130, 246, 0.5)';" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='none';">
            <svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            Contacter Inoveo3D
        </a>
    `;

    document.body.appendChild(errorDiv);
}

// Cleanup on page unload
function cleanup() {
    // Only cleanup if viewer is actually initialized
    if (window.viewer360 && window.viewer360.isInitialized) {
        console.log('[Bootstrap] Cleaning up viewer...');
        window.viewer360.destroy();
    }
}

// Bootstrap when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
} else {
    bootstrap();
}

// Cleanup on page unload
window.addEventListener('beforeunload', cleanup);

// Handle Vite HMR (Hot Module Replacement)
if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        cleanup();
    });
}
